import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl, formatRupiah } from "@/lib/api";
import { Building2, Filter, Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const STATUS_LABELS = {
  baru: "Baru", on_market: "On Market", nego: "Negosiasi", closing: "Closing",
  sold: "SOLD", reserved: "Reserved",
};
const STATUS_COLORS = {
  baru: "bg-blue-500/15 text-blue-300 border-blue-500/20",
  on_market: "bg-emerald-500/15 text-emerald-300 border-emerald-500/20",
  nego: "bg-amber-500/15 text-amber-300 border-amber-500/20",
  closing: "bg-purple-500/15 text-purple-300 border-purple-500/20",
  sold: "bg-slate-600/30 text-slate-300 border-slate-500/20",
  reserved: "bg-pink-500/15 text-pink-300 border-pink-500/20",
};

export default function Listings() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/listings?mine=true");
      setListings(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return listings.filter((l) => {
      if (search && !`${l.title} ${l.location}`.toLowerCase().includes(search.toLowerCase())) return false;
      if (filterType !== "all" && l.property_type !== filterType) return false;
      if (filterStatus !== "all" && l.status !== filterStatus) return false;
      return true;
    });
  }, [listings, search, filterType, filterStatus]);

  return (
    <div className="space-y-6" data-testid="listings-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <span className="text-xs uppercase tracking-widest text-orange-400 font-semibold">Portofolio</span>
          <h1 className="text-3xl font-extrabold tracking-tight mt-1">Listing Saya</h1>
        </div>
        <Link to="/app/listings/new">
          <Button data-testid="listings-new-btn" className="bg-gradient-to-r from-[#FF5722] to-[#F97316] hover:from-[#F97316] hover:to-[#EA580C] font-semibold">
            <Plus className="w-4 h-4 mr-1" /> Tambah Listing
          </Button>
        </Link>
      </div>

      <div className="rounded-xl bg-[#141C2E] border border-white/5 p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
                 data-testid="listings-search-input"
                 className="pl-9 bg-[#0D1322] border-white/10" placeholder="Cari judul atau lokasi…" />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger data-testid="listings-type-filter" className="w-[160px] bg-[#0D1322] border-white/10">
            <Filter className="w-3.5 h-3.5 mr-1" /><SelectValue placeholder="Tipe" />
          </SelectTrigger>
          <SelectContent className="bg-[#141C2E] border-white/10 text-slate-100">
            <SelectItem value="all">Semua Tipe</SelectItem>
            <SelectItem value="rumah">Rumah</SelectItem>
            <SelectItem value="apartemen">Apartemen</SelectItem>
            <SelectItem value="vila">Vila</SelectItem>
            <SelectItem value="ruko">Ruko</SelectItem>
            <SelectItem value="lahan">Lahan</SelectItem>
            <SelectItem value="lainnya">Lainnya</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger data-testid="listings-status-filter" className="w-[160px] bg-[#0D1322] border-white/10">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-[#141C2E] border-white/10 text-slate-100">
            <SelectItem value="all">Semua Status</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="h-40 rounded-xl bg-[#141C2E] animate-pulse" />
      ) : filtered.length === 0 ? (
        <div className="rounded-xl bg-[#141C2E] border border-white/5 p-16 text-center">
          <Building2 className="w-10 h-10 mx-auto text-slate-500 mb-3" />
          <h3 className="font-semibold text-lg">Belum ada listing</h3>
          <p className="text-slate-400 text-sm mt-1">Mulai dari listing pertama Anda.</p>
          <Link to="/app/listings/new">
            <Button className="mt-5 bg-orange-500 hover:bg-orange-600" data-testid="empty-new-listing-btn">
              <Plus className="w-4 h-4 mr-1" /> Tambah Listing
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.map((l) => <ListingCard key={l.id} l={l} />)}
        </div>
      )}
    </div>
  );
}

export function ListingCard({ l }) {
  const img = l.photos?.[0];
  return (
    <Link to={`/app/listings/${l.id}`}
          data-testid={`listing-card-${l.id}`}
          className="group block rounded-xl overflow-hidden border border-white/5 bg-[#141C2E] hover:border-orange-500/40 hover:shadow-lg hover:shadow-orange-500/5 transition-all">
      <div className="aspect-[4/3] bg-slate-800 overflow-hidden relative">
        {img ? (
          <img src={fileUrl(img)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
        ) : (
          <div className="w-full h-full grid place-items-center text-slate-600"><Building2 className="w-10 h-10" /></div>
        )}
        <div className="absolute top-3 left-3">
          <Badge className={`${STATUS_COLORS[l.status]} border font-semibold uppercase text-[10px] tracking-wider`}>{STATUS_LABELS[l.status]}</Badge>
        </div>
      </div>
      <div className="p-4">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{l.property_type}</div>
        <h3 className="font-semibold text-slate-100 line-clamp-1 mt-0.5">{l.title}</h3>
        <div className="text-xs text-slate-400 line-clamp-1">{l.location}</div>
        <div className="mt-3 flex items-center justify-between">
          <div className="text-orange-400 font-bold">{formatRupiah(l.price)}</div>
          <div className="text-xs text-slate-500 flex gap-2">
            {l.bedrooms != null && <span>🛏 {l.bedrooms}</span>}
            {l.bathrooms != null && <span>🚿 {l.bathrooms}</span>}
          </div>
        </div>
      </div>
    </Link>
  );
}
